import { NmLoader } from '../../components/NmLoader';
import { useAppBack } from '../../hooks/useAppBack';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarPage } from './CalendarPage';
import { WorkoutFormModal } from '../../pages/WorkoutFormModal';
import { EventFormModal } from '../../pages/EventFormModal';
import { MetricFormModal } from '../../pages/MetricFormModal';
import { NoteFormModal } from '../../pages/NoteFormModal';
import { useAthleteContext } from '../../context/AthleteContext';
import { todayJalali, jalaliToGregorian, formatGregorian } from '../../utils/jalali';
import type { CalendarEvent } from '../../services/api';
import { forms } from '../../services/api';

function gregorianToday(): string {
  const j = todayJalali();
  const g = jalaliToGregorian(j.year, j.month, j.dat);
  return formatGregorian(new Date(g.year, g.month - 1, g.day));
}

function FormOverlay({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(26,31,43,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          background: 'var(--surface-0)',
          boxShadow: 'var(--nm-raised-lg)',
          padding: 'var(--sp-4)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function useCalendarFormSuccess() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (toastKey: string) => {
    navigate('/calendar', {
      replace: true,
      state: {
        toast: t(toastKey),
        refreshCalendar: true,
      },
    });
  };
}

export function CalendarLayout() {
  return (
    <>
      <CalendarPage />
      <Outlet />
    </>
  );
}

function useRouteDate(): string {
  const [params] = useSearchParams();
  return params.get('date') ?? gregorianToday();
}

function useRouteUniqueCode(): string | null {
  const [params] = useSearchParams();
  return params.get('code');
}

function useRouteEventId(): number | null {
  const [params] = useSearchParams();
  const raw = params.get('id') ?? params.get('eventId');
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function NewWorkoutRoute() {
  const close = useAppBack('/calendar');
  const { effectiveUserId } = useAthleteContext();
  const date = useRouteDate();
  const onFormSuccess = useCalendarFormSuccess();
  if (!effectiveUserId) return null;
  return (
    <WorkoutFormModal
      userId={effectiveUserId}
      date={date}
      onClose={close}
      onSuccess={() => onFormSuccess('calendar.toast.workoutAdded')}
    />
  );
}

export function NewEventRoute() {
  const close = useAppBack('/calendar');
  const location = useLocation();
  const { effectiveUserId, isCoach } = useAthleteContext();
  const date = useRouteDate();
  const eventId = useRouteEventId();
  const onFormSuccess = useCalendarFormSuccess();
  const { t } = useTranslation();

  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const fromState = (location.state as { event?: CalendarEvent } | null)?.event;
    if (fromState && (!eventId || fromState.id === eventId)) {
      setEvent(fromState);
      setLoadError(false);
      setLoading(false);
      return;
    }

    if (!eventId) {
      setEvent(null);
      setLoadError(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    void forms
      .getEvent(eventId, { asAthlete: !isCoach })
      .then((res) => {
        if (cancelled) return;
        setEvent(res.data?.data?.event ?? null);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, isCoach, location.state]);

  if (!effectiveUserId) return null;

  if (loading) {
    return (
      <FormOverlay onClose={close}>
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)' }}>
          <NmLoader size="sm" inline label={t('common.loading', 'Loading…')} />
        </div>
      </FormOverlay>
    );
  }

  if (loadError) {
    return (
      <FormOverlay onClose={close}>
        <div style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
          {t('eventForm.error.loadFailed', 'Failed to load event')}
        </div>
      </FormOverlay>
    );
  }

  const isEdit = event != null;

  return (
    <EventFormModal
      userId={effectiveUserId}
      date={date}
      editableDate
      event={event}
      onClose={close}
      onSuccess={() =>
        onFormSuccess(isEdit ? 'calendar.toast.eventUpdated' : 'calendar.toast.eventAdded')
      }
    />
  );
}

export function NewMetricRoute() {
  const close = useAppBack('/calendar');
  const { effectiveUserId } = useAthleteContext();
  const date = useRouteDate();
  const uniqueCode = useRouteUniqueCode();
  const onFormSuccess = useCalendarFormSuccess();
  if (!effectiveUserId) return null;
  return (
    <FormOverlay onClose={close}>
      <MetricFormModal
        userId={effectiveUserId}
        date={date}
        uniqueCode={uniqueCode}
        onClose={close}
        onSuccess={() => onFormSuccess(uniqueCode ? 'calendar.toast.metricUpdated' : 'calendar.toast.metricAdded')}
      />
    </FormOverlay>
  );
}

export function NewNoteRoute() {
  const close = useAppBack('/calendar');
  const { effectiveUserId } = useAthleteContext();
  const date = useRouteDate();
  const onFormSuccess = useCalendarFormSuccess();
  if (!effectiveUserId) return null;
  return (
    <FormOverlay onClose={close}>
      <NoteFormModal
        userId={effectiveUserId}
        date={date}
        onClose={close}
        onSuccess={() => onFormSuccess('calendar.toast.noteAdded')}
      />
    </FormOverlay>
  );
}
